import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from 'screens/Admin/Analytics/components/TimeRangeDropdown';
import { formatBucketTick, formatBucketLabel } from 'screens/Admin/Analytics/utils/formatBucket';
import {
  PaletteName,
  SINGLE_DEFAULT,
  OTHER_LABEL,
  resolveSegmentColors
} from './palettes';

// One flat row as returned by the chart endpoints. `dimension` is omitted/null in
// single-series mode; present (e.g. a source or username) in segmented mode.
export interface StackedBarChartRow {
  date: string;
  dimension?: string | null;
  value: number;
}

export interface StackedBarTotals {
  total: number;
  bySegment: Record<string, number>;
  segments: string[];
}

interface Props {
  title: string;
  subtitle?: string;
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
  data?: StackedBarChartRow[];
  isLoading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  mode?: 'single' | 'segmented';
  /** Named palette or an explicit { segment: hex } map for fixed cross-chart colors. */
  colorPalette?: PaletteName | Record<string, string>;
  /** Bar color for single-series mode. */
  singleColor?: string;
  yAxisLabel?: string;
  /** Noun used in the default tooltip/footer, e.g. "cards", "alerts". */
  valueNoun?: string;
  /** Override the footer entirely, or supply a template fed the computed totals. */
  footer?: React.ReactNode;
  footerTemplate?: (totals: StackedBarTotals, period: AnalyticsPeriod) => React.ReactNode;
  /** Override the top-right legend; defaults to one swatch per segment (cap 4 + "+N more"). */
  legend?: React.ReactNode;
  emptyMessage?: string;
}

const periodNoun = (period: AnalyticsPeriod): string =>
  period === 7 ? 'week' : period === 30 ? 'month' : 'year';

const StackedBarChart: React.FC<Props> = ({
  title,
  subtitle,
  period,
  onPeriodChange,
  data,
  isLoading,
  error,
  onRetry,
  mode = 'segmented',
  colorPalette = 'categorical-4',
  singleColor = SINGLE_DEFAULT,
  yAxisLabel,
  valueNoun = 'items',
  footer,
  footerTemplate,
  legend,
  emptyMessage
}) => {
  // Segments ordered by total descending (largest stacks at the bottom); "Other" always last.
  const { segments, bySegment, total } = useMemo<StackedBarTotals & { bySegment: Record<string, number> }>(() => {
    const totals: Record<string, number> = {};
    let grand = 0;
    (data || []).forEach((row) => {
      grand += row.value;
      if (mode === 'single') return;
      const dim = row.dimension ?? 'unknown';
      totals[dim] = (totals[dim] || 0) + row.value;
    });
    const segs = Object.keys(totals).sort((a, b) => {
      if (a === OTHER_LABEL) return 1;
      if (b === OTHER_LABEL) return -1;
      return totals[b] - totals[a];
    });
    return { segments: segs, bySegment: totals, total: grand };
  }, [data, mode]);

  // Pivot flat rows into one object per date with a key per segment (recharts shape).
  const chartData = useMemo(() => {
    const byDate: Record<string, Record<string, number | string>> = {};
    (data || []).forEach((row) => {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      if (mode === 'single') {
        byDate[row.date].value = (Number(byDate[row.date].value) || 0) + row.value;
      } else {
        const dim = row.dimension ?? 'unknown';
        byDate[row.date][dim] = (Number(byDate[row.date][dim]) || 0) + row.value;
      }
    });
    return Object.values(byDate).sort((a, b) =>
      String(a.date) < String(b.date) ? -1 : 1
    );
  }, [data, mode]);

  const colors = useMemo(
    () => (mode === 'single' ? {} : resolveSegmentColors(segments, colorPalette)),
    [segments, colorPalette, mode]
  );

  const defaultLegend = useMemo(() => {
    if (mode === 'single') {
      return (
        <div className="chart-legend">
          <span className="legend-dot" style={{ backgroundColor: singleColor }} />
          <span className="legend-label">{valueNoun}</span>
        </div>
      );
    }
    const shown = segments.slice(0, 4);
    return (
      <div className="chart-legend-multi">
        {shown.map((seg) => (
          <div key={seg} className="chart-legend">
            <span className="legend-dot" style={{ backgroundColor: colors[seg] }} />
            <span className="legend-label">{seg}</span>
          </div>
        ))}
        {segments.length > shown.length && (
          <span className="legend-label">+{segments.length - shown.length} more</span>
        )}
      </div>
    );
  }, [mode, segments, colors, singleColor, valueNoun]);

  const resolvedFooter = useMemo(() => {
    if (footer !== undefined) return footer;
    if (!data) return null;
    if (footerTemplate) {
      return footerTemplate({ total, bySegment, segments }, period);
    }
    return `Total: ${total.toLocaleString()} ${valueNoun} in the last ${periodNoun(period)}`;
  }, [footer, footerTemplate, data, total, bySegment, segments, period, valueNoun]);

  const isEmpty = !!data && total === 0;

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>{title}</h2>
          {subtitle && <p className="chart-subtitle">{subtitle}</p>}
        </div>
        <div className="chart-header-right">
          {legend ?? defaultLegend}
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">
            Failed to load data
            {onRetry && (
              <button className="chart-retry-btn" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        ) : data ? (
          <div className="chart-empty-wrap">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatBucketTick}
                  stroke="#666"
                  tick={{ fill: '#999', fontSize: 12 }}
                  axisLine={{ stroke: '#2a2a2a' }}
                  tickLine={false}
                  interval={chartData.length > 60 ? Math.floor(chartData.length / 12) : 0}
                />
                <YAxis
                  stroke="#666"
                  tick={{ fill: '#999', fontSize: 12 }}
                  axisLine={{ stroke: '#2a2a2a' }}
                  tickLine={false}
                  allowDecimals={false}
                  tickFormatter={(value) => Number(value).toLocaleString()}
                  label={
                    yAxisLabel
                      ? {
                          value: yAxisLabel,
                          angle: -90,
                          position: 'insideLeft',
                          style: { fill: '#999', fontSize: 12, textAnchor: 'middle' }
                        }
                      : undefined
                  }
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                  labelFormatter={(label) => formatBucketLabel(label as string, period)}
                  formatter={(value, name) => [
                    `${Number(value).toLocaleString()}`,
                    mode === 'single' ? valueNoun : (name as string)
                  ]}
                />
                {mode === 'single' ? (
                  <Bar dataKey="value" fill={singleColor} radius={[4, 4, 0, 0]} name={valueNoun} />
                ) : (
                  segments.map((seg, index) => (
                    <Bar
                      key={seg}
                      dataKey={seg}
                      stackId="stack"
                      fill={colors[seg]}
                      radius={index === segments.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      name={seg}
                    />
                  ))
                )}
              </BarChart>
            </ResponsiveContainer>
            {isEmpty && (
              <div className="chart-empty">{emptyMessage || 'No data in this period'}</div>
            )}
          </div>
        ) : null}
      </div>

      {resolvedFooter != null && <div className="chart-summary">{resolvedFooter}</div>}
    </div>
  );
};

export default StackedBarChart;
